import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Fixed } from 'layouts';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  InputAdornment,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh, Search } from '@mui/icons-material';
import moment from 'moment';
import getRecallBotUsage from 'redux/actions/Common/getRecallBotUsage';
import useOrgSearch from './useOrgSearch';

// Every bot counts once in `created` and in exactly one state column, so the
// state columns sum to `created`. `completedWithRecording` is a subset of
// `completed` and is shown inside that column rather than as its own.
const COUNT_COLUMNS = [
  { key: 'created', label: 'Created', help: 'Every bot created' },
  {
    key: 'scheduled',
    label: 'Scheduled',
    help: 'Not yet joined; join time is upcoming or under 1 hour ago',
  },
  {
    key: 'completed',
    label: 'Completed (with recording)',
    help: 'Recall finished the meeting; bracketed count produced a recording',
  },
  {
    key: 'cancelled',
    label: 'Cancelled',
    help: 'Meeting deleted or rescheduled, bot replaced, or cancelled by user',
  },
  {
    key: 'failed',
    label: 'Failed',
    help: 'Fatal/expired status, or never joined over 1 hour after join time',
  },
  {
    key: 'inProgress',
    label: 'In progress',
    help: 'Joining, waiting, in the call, or ended and awaiting Recall',
  },
];

const sumBy = (rows, key) =>
  rows.reduce((total, row) => total + (Number(row?.[key]) || 0), 0);

const RecallBotUsage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });
  const orgSearch = useOrgSearch();

  const [usage, setUsage] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [filterOrg, setFilterOrg] = useState(null);
  // `yyyy-MM`; empty means all time.
  const [month, setMonth] = useState('');
  const [keyword, setKeyword] = useState('');
  const [alert, setAlert] = useState(null);
  // Re-renders the "Last computed X ago" label without refetching.
  const [, setNow] = useState(Date.now());
  // Drops responses from requests superseded by a newer filter change.
  const requestSeq = useRef(0);

  const fetchUsage = ({
    orgId = filterOrg?.id,
    selectedMonth = month,
    forceRefresh = false,
  } = {}) => {
    const seq = ++requestSeq.current;
    if (forceRefresh) setRefreshing(true);
    else setFetching(true);
    dispatch(
      getRecallBotUsage(
        { orgId, month: selectedMonth, forceRefresh },
        (data) => {
          if (seq !== requestSeq.current) return;
          setUsage(Array.isArray(data) ? data : []);
          setFetching(false);
          setRefreshing(false);
        },
        (message) => {
          if (seq !== requestSeq.current) return;
          setFetching(false);
          setRefreshing(false);
          setAlert({ severity: 'error', message });
        },
      ),
    );
  };

  useEffect(() => {
    fetchUsage();
    const timer = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  const computedTimes = usage
    .map((row) => row?.computedOn)
    .filter((time) => Number.isFinite(time));
  const oldestComputedOn = computedTimes.length
    ? Math.min(...computedTimes)
    : null;

  const trimmedKeyword = keyword.trim().toLowerCase();
  const rows = usage.filter((row) => {
    if (!trimmedKeyword) return true;
    return [row?.name, row?.email, row?.userId].some((value) =>
      value?.toLowerCase()?.includes(trimmedKeyword),
    );
  });

  const renderCount = (row, key) =>
    key === 'completed'
      ? `${row?.completed ?? 0} (${row?.completedWithRecording ?? 0})`
      : row?.[key] ?? 0;

  return (
    <Fixed>
      <Box
        width={{ xs: '100vw', sm: '100vw', md: 'calc(100vw - 266px)' }}
        height={{ xs: '100vh', sm: '100vh', md: 'calc(100vh - 80px)' }}
        sx={{
          overflow: 'scroll',
          padding: '20px',
        }}
        display={'flex'}
        flexDirection={'column'}
        position={'relative'}
      >
        <Box
          display={'flex'}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent={'space-between'}
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={2}
        >
          <Box>
            <Typography variant={'h6'} fontWeight={'bold'}>
              Recall Bot Usage
            </Typography>
            <Typography variant={'body2'} color={'text.secondary'}>
              Meeting bots per user, by state.{' '}
              {oldestComputedOn
                ? `Last computed ${moment(oldestComputedOn).fromNow()}.`
                : null}
            </Typography>
          </Box>
          <Box display={'flex'} alignItems={'center'} gap={1} flexWrap={'wrap'}>
            <Autocomplete
              size="small"
              sx={{ minWidth: isMd ? 260 : 200 }}
              options={orgSearch.organizations}
              value={filterOrg}
              loading={orgSearch.fetching}
              filterOptions={(options) => options}
              getOptionLabel={(org) => org?.organizationName || org?.id || ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              onInputChange={(e, value, reason) => {
                if (reason === 'input') orgSearch.setKeyword(value);
                if (reason === 'clear') orgSearch.setKeyword('');
              }}
              onChange={(e, org) => {
                setFilterOrg(org);
                fetchUsage({ orgId: org?.id });
              }}
              renderInput={(params) => (
                <TextField {...params} placeholder="All users with bots" />
              )}
            />
            <TextField
              size="small"
              type="month"
              label={month ? 'Month' : 'Month (all time)'}
              value={month}
              onChange={(e) => {
                setMonth(e.target.value);
                fetchUsage({ selectedMonth: e.target.value });
              }}
              InputLabelProps={{ shrink: true }}
            />
            {month ? (
              <Button
                size="small"
                onClick={() => {
                  setMonth('');
                  fetchUsage({ selectedMonth: '' });
                }}
                sx={{ whiteSpace: 'nowrap' }}
              >
                All time
              </Button>
            ) : null}
            <Tooltip title="Recompute the counts now instead of using the hourly cache. May take a few seconds.">
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => fetchUsage({ forceRefresh: true })}
                  disabled={fetching || refreshing}
                  startIcon={
                    refreshing ? (
                      <CircularProgress size={14} />
                    ) : (
                      <Refresh fontSize="small" />
                    )
                  }
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {refreshing ? 'Refreshing...' : 'Refresh'}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        <Box mt={3}>
          <TextField
            size="small"
            placeholder="Filter by name, email or user id..."
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: isMd ? 320 : 'auto' }}
          />
        </Box>

        {fetching ? (
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent={'center'}
            gap={1}
            py={6}
          >
            <CircularProgress size={20} />
            <Typography variant={'body2'} color={'text.secondary'}>
              Loading Recall bot usage...
            </Typography>
          </Box>
        ) : usage.length > 0 ? (
          <TableContainer
            component={Paper}
            sx={{ marginTop: 2, opacity: refreshing ? 0.6 : 1 }}
          >
            <Table
              size="small"
              sx={{ minWidth: 800 }}
              aria-label="recall bot usage table"
            >
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  {COUNT_COLUMNS.map((column) => (
                    <TableCell key={column.key} align="center">
                      <Tooltip title={column.help}>
                        <span>{column.label}</span>
                      </Tooltip>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length > 0 ? (
                  rows.map((row) => (
                    <TableRow key={row?.userId}>
                      <TableCell component="th" scope="row">
                        {/* Without an org filter, rows carry no name/email. */}
                        <Typography variant={'body2'} fontWeight={'bold'}>
                          {row?.name || row?.email || row?.userId || '--'}
                        </Typography>
                        {row?.name && row?.email ? (
                          <Typography
                            variant={'caption'}
                            color={'text.secondary'}
                          >
                            {row.email}
                          </Typography>
                        ) : null}
                      </TableCell>
                      {COUNT_COLUMNS.map((column) => (
                        <TableCell key={column.key} align="center">
                          {renderCount(row, column.key)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={COUNT_COLUMNS.length + 1}
                      align="center"
                    >
                      <Typography
                        variant={'body2'}
                        color={'text.secondary'}
                        py={2}
                      >
                        No users match this filter.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              {rows.length > 1 ? (
                <TableFooter>
                  <TableRow>
                    <TableCell>
                      <Typography variant={'body2'} fontWeight={'bold'}>
                        Total ({rows.length} users)
                      </Typography>
                    </TableCell>
                    {COUNT_COLUMNS.map((column) => (
                      <TableCell key={column.key} align="center">
                        <Typography variant={'body2'} fontWeight={'bold'}>
                          {column.key === 'completed'
                            ? `${sumBy(rows, 'completed')} (${sumBy(
                                rows,
                                'completedWithRecording',
                              )})`
                            : sumBy(rows, column.key)}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableFooter>
              ) : null}
            </Table>
          </TableContainer>
        ) : (
          <Box
            display={'flex'}
            flexDirection={'column'}
            alignItems={'center'}
            justifyContent={'center'}
            gap={1}
            py={6}
          >
            <Typography variant={'subtitle1'} fontWeight={'bold'}>
              No bot usage
            </Typography>
            <Typography variant={'body2'} color={'text.secondary'}>
              {filterOrg
                ? 'This organization has no members.'
                : 'No user has had a Recall bot in this period.'}
            </Typography>
          </Box>
        )}

        <Snackbar
          open={Boolean(alert)}
          autoHideDuration={4000}
          onClose={() => setAlert(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setAlert(null)}
            severity={alert?.severity || 'info'}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {alert?.message}
          </Alert>
        </Snackbar>
      </Box>
    </Fixed>
  );
};

export default RecallBotUsage;
